import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, AlertTriangle, XCircle, Clock } from 'lucide-react';
import { enhancedSyncService, SyncStatusType, SyncProgress } from '../services/enhancedSyncService';

interface SyncStatusIndicatorProps {
  className?: string;
  showDetails?: boolean; // Show detailed stats
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Compact sync status indicator component
 * Shows current sync status with icon and optional details
 * Can be placed in header/footer or anywhere in the UI
 */
const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({ 
  className = '', 
  showDetails = false,
  size = 'md'
}) => {
  const [syncStatus, setSyncStatus] = useState<SyncStatusType>('idle');
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Listen to sync status updates
  useEffect(() => {
    const handleSyncStatus = (event: CustomEvent<SyncStatusType>) => {
      setSyncStatus(event.detail);
      if (event.detail === 'completed') {
        setLastSyncTime(new Date());
      }
    };

    const handleSyncProgress = (event: CustomEvent<SyncProgress>) => {
      setSyncProgress(event.detail);
    };

    window.addEventListener('sync-status-changed' as any, handleSyncStatus);
    window.addEventListener('sync-progress' as any, handleSyncProgress);

    // Initial load
    loadPendingCount();

    // Refresh pending count every 30 seconds
    const interval = setInterval(loadPendingCount, 30000);

    return () => {
      window.removeEventListener('sync-status-changed' as any, handleSyncStatus);
      window.removeEventListener('sync-progress' as any, handleSyncProgress);
      clearInterval(interval);
    };
  }, []);

  // Update pending count
  const loadPendingCount = async () => {
    try {
      const stats = await enhancedSyncService.getPendingStats();
      setPendingCount(stats.total);
    } catch (error) {
      console.error('Failed to load pending count:', error);
    }
  };

  // Update pending count when sync completes/fails
  useEffect(() => {
    if (syncStatus === 'completed' || syncStatus === 'failed') {
      loadPendingCount();
    }
  }, [syncStatus]);

  // Manual sync trigger
  const handleManualSync = async () => {
    try {
      await enhancedSyncService.syncNow();
    } catch (error) {
      console.error('Manual sync failed:', error);
    }
  };

  // Get icon size based on size prop
  const iconSize = size === 'sm' ? 16 : size === 'md' ? 20 : 24;
  const textSize = size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base';

  // Get status icon and color
  const getStatusDisplay = () => {
    switch (syncStatus) {
      case 'syncing':
        return {
          icon: <RefreshCw size={iconSize} className="animate-spin" />,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          label: 'Syncing...'
        };
      case 'completed':
        return {
          icon: <CheckCircle size={iconSize} />,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          label: 'Synced'
        };
      case 'failed':
        return {
          icon: <XCircle size={iconSize} />,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          label: 'Failed'
        };
      case 'paused':
        return {
          icon: <AlertTriangle size={iconSize} />,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          label: 'Paused'
        };
      default:
        return pendingCount > 0 ? {
          icon: <Clock size={iconSize} />,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          label: 'Pending'
        } : {
          icon: <CheckCircle size={iconSize} />,
          color: 'text-gray-400',
          bgColor: 'bg-gray-50',
          label: 'Idle'
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  // Format time ago
  const formatTimeAgo = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Status Icon */}
      <div 
        className={`flex items-center justify-center p-2 rounded-full ${statusDisplay.bgColor} ${statusDisplay.color} cursor-pointer transition-all hover:opacity-80`}
        onClick={handleManualSync}
        title={`Click to sync manually. Status: ${statusDisplay.label}`}
      >
        {statusDisplay.icon}
      </div>

      {/* Details (optional) */}
      {showDetails && (
        <div className={`flex flex-col ${textSize}`}>
          <div className="flex items-center gap-2">
            <span className={`font-semibold ${statusDisplay.color}`}>
              {statusDisplay.label}
            </span>
            {pendingCount > 0 && (
              <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-bold">
                {pendingCount}
              </span>
            )}
          </div>

          {/* Progress bar during sync */}
          {syncStatus === 'syncing' && syncProgress && (
            <div className="mt-1">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-0.5">
                <span>{syncProgress.current} / {syncProgress.total}</span>
                {syncProgress.eta && (
                  <span>{Math.ceil(syncProgress.eta / 1000)}s</span>
                )}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div
                  className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                  style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Last sync time */}
          {lastSyncTime && syncStatus !== 'syncing' && (
            <span className="text-xs text-gray-500 mt-0.5">
              Last sync: {formatTimeAgo(lastSyncTime)}
            </span>
          )}

          {/* Failed message */}
          {syncStatus === 'failed' && (
            <span className="text-xs text-red-600 mt-0.5">
              Sync failed. Click to retry.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SyncStatusIndicator;
